import { useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth.jsx";
import { Card, SectionHeader } from "../components/Card";
import { getSupabase } from "../lib/supabase";

export function ChatScreen() {
  const { authedFetch, session } = useAuth();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [typing, setTyping] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimer = useRef(null);
  const channelRef = useRef(null);

  async function loadThreads() {
    try { const out = await authedFetch("/api/chat/threads"); setThreads(out.threads); }
    catch (e) { setErr(e.message); }
  }
  async function loadMessages(threadId) {
    try {
      const out = await authedFetch(`/api/chat/threads/${threadId}/messages`);
      setMessages(out.messages);
      setTimeout(() => { scrollRef.current?.scrollTo({ top: 99999 }); }, 50);
    } catch (e) { setErr(e.message); }
  }
  async function send() {
    if (!text.trim() || !active || sending) return;
    setSending(true);
    const msg = text.trim();
    setText(""); // Clear immediately for better UX
    try {
      const resp = await authedFetch(`/api/chat/threads/${active.id}/messages`, {
        method: "POST", body: JSON.stringify({ body: msg }),
      });
      setMessages(prev => [...prev, resp.message]);
      setTimeout(() => scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }), 50);
    } catch (e) {
      setErr(e.message);
      setText(msg); // Restore message on error
    }
    setSending(false);
  }

  // Auto-focus input when thread changes
  useEffect(() => {
    if (active) inputRef.current?.focus();
  }, [active]);

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => {
    if (!threads.length) {
      setSelectedGroupId(null);
      return;
    }
    setSelectedGroupId((current) => {
      if (current && threads.some((thread) => otherId(thread, session?.user?.id) === current)) return current;
      return otherId(threads[0], session?.user?.id);
    });
  }, [threads, session?.user?.id]);
  useEffect(() => {
    if (!active) return;
    loadMessages(active.id);
    const id = setInterval(() => loadMessages(active.id), 2_500);
    return () => clearInterval(id);
  }, [active]);

  useEffect(() => {
    if (!active || !session?.user?.id) return;
    let channel;
    let cancelled = false;
    (async () => {
      const sb = await getSupabase();
      if (cancelled) return;
      channel = sb.channel(`chat-thread-${active.id}`)
        .on(
          "postgres_changes",
          { event: "INSERT", schema: "public", table: "chat_messages", filter: `thread_id=eq.${active.id}` },
          (payload) => {
            const msg = payload.new;
            setMessages((prev) => prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]);
            setTimeout(() => scrollRef.current?.scrollTo({ top: 99999, behavior: "smooth" }), 40);
          }
        )
        .on("broadcast", { event: "typing" }, ({ payload }) => {
          if (payload?.userId === session.user.id) return;
          setTyping(Boolean(payload?.typing));
          window.clearTimeout(typingTimer.current);
          typingTimer.current = window.setTimeout(() => setTyping(false), 1800);
        })
        .subscribe();
      channelRef.current = channel;
    })();
    return () => {
      cancelled = true;
      window.clearTimeout(typingTimer.current);
      channelRef.current = null;
      if (channel) getSupabase().then((sb) => sb.removeChannel(channel));
    };
  }, [active?.id, session?.user?.id]);

  async function broadcastTyping(isTyping) {
    if (!active || !session?.user?.id) return;
    await channelRef.current?.send({
      type: "broadcast",
      event: "typing",
      payload: { userId: session.user.id, typing: isTyping },
    });
  }

  const contactGroups = groupThreadsByContact(threads, session?.user?.id);
  const selectedGroup = contactGroups.find((group) => group.id === selectedGroupId) || contactGroups[0] || null;

  function selectGroup(group) {
    setSelectedGroupId(group.id);
    setActive(group.threads.length === 1 ? group.threads[0] : null);
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", paddingBottom: 80 }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "30px 16px" }}>
        <SectionHeader emoji="💬" title="Chat" subtitle="Message about your booking before, during, and after." />

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
          gap: 16,
          minHeight: 520
        }}>
          <Card style={{ padding: 0, overflow: "hidden", maxWidth: "none", margin: 0, minHeight: 520 }}>
            <div style={{
              padding: "14px 16px",
              borderBottom: "1px solid var(--border)",
              background: "var(--bg-elevated)",
              position: "sticky",
              top: 0,
              zIndex: 10
            }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Conversations</div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                {contactGroups.length} {contactGroups.length === 1 ? "contact" : "contacts"} · {threads.length} {threads.length === 1 ? "chat" : "chats"}
              </div>
            </div>
            <div style={{ maxHeight: 520, overflowY: "auto" }}>
              {contactGroups.length === 0 && (
                <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>No conversations yet</div>
                  <div style={{ fontSize: 13 }}>A chat opens automatically when you book.</div>
                </div>
              )}
	              {contactGroups.map((group) => {
	                const isActive = selectedGroup?.id === group.id;
	                const desName = group.threads[0]?.booking?.request?.final_design?.name;
	                const latestLabel = formatShortDateTime(group.latestDate);
	                return (
                  <button key={group.id} onClick={() => selectGroup(group)}
                    style={{
                      display: "block", width: "100%", textAlign: "left", padding: "14px 16px",
                      background: isActive ? "rgba(196,135,154,0.12)" : "transparent",
                      border: "none",
                      borderLeft: isActive ? "3px solid var(--mauve)" : "3px solid transparent",
                      borderBottom: "1px solid var(--border)",
                      cursor: "pointer",
                      color: "var(--text)",
                      transition: "all 0.15s ease",
                      position: "relative"
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.background = "rgba(196,135,154,0.05)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.background = "transparent";
                    }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {group.avatar_url ? (
                        <img
                          src={group.avatar_url}
                          alt={group.name}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: "50%",
                            objectFit: "cover",
                            flexShrink: 0,
                            border: "2px solid var(--border)"
                          }}
                        />
                      ) : (
                        <div style={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 16,
                          flexShrink: 0
                        }}>
                          {initialsFor(group.name)}
                        </div>
                      )}
	                      <div style={{ flex: 1, minWidth: 0 }}>
	                        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
	                          <div style={{ fontWeight: 700, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
	                            {group.name}
	                          </div>
	                          {latestLabel && (
	                            <time dateTime={new Date(group.latestDate).toISOString()} style={{ color: "var(--text-muted)", fontSize: 11, whiteSpace: "nowrap", flexShrink: 0 }}>
	                              {latestLabel}
	                            </time>
	                          )}
	                        </div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {group.threads.length} {group.threads.length === 1 ? "chat" : "chats"} · {desName || "Booking chats"}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          <Card style={{ padding: 0, overflow: "hidden", maxWidth: "none", margin: 0, display: "flex", flexDirection: "column", height: 600 }}>
            {!active ? (
              <div style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                color: "var(--text)",
              }}>
                {selectedGroup ? (
                  <>
                    <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)", background: "var(--bg-elevated)" }}>
                      <div style={{ fontWeight: 800, fontSize: 15 }}>{selectedGroup.name}</div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
                        {selectedGroup.threads.length} booking {selectedGroup.threads.length === 1 ? "chat" : "chats"}
                      </div>
                    </div>
                    <div style={{ padding: 14, display: "grid", gap: 10, overflowY: "auto" }}>
	                      {selectedGroup.threads.map((thread) => {
	                        const threadTime = formatShortDateTime(thread.last_message_at || thread.booking?.created_at);
	                        return (
	                        <button
	                          key={thread.id}
                          onClick={() => setActive(thread)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 12,
                            width: "100%",
                            textAlign: "left",
                            border: "1px solid var(--border)",
                            borderRadius: 14,
                            background: "var(--bg-soft)",
                            color: "var(--text)",
                            padding: "12px 14px",
                            cursor: "pointer",
                          }}
                        >
	                          <div style={{ minWidth: 0 }}>
	                            <div style={{ fontWeight: 800, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
	                              {thread.booking?.request?.final_design?.name || "Booking chat"}
	                            </div>
	                            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 3 }}>
	                              {thread.booking?.status ? formatThreadStatus(thread.booking.status) : "Booking"}
	                              {threadTime ? ` · ${threadTime}` : ""}
	                            </div>
	                          </div>
	                          <span style={{ color: "var(--text-muted)", fontSize: 18 }}>›</span>
	                        </button>
	                        );
	                      })}
                    </div>
                  </>
                ) : (
                  <div style={{ flex: 1, display: "grid", placeItems: "center", color: "var(--text-muted)", padding: 32, textAlign: "center" }}>
                    Pick a conversation from the sidebar to start chatting.
                  </div>
                )}
              </div>
            ) : (
              <>
                <div style={{
                  padding: "12px 16px",
                  borderBottom: "1px solid var(--border)",
                  background: "var(--bg-elevated)",
                  display: "flex",
                  alignItems: "center",
                  gap: 10
                }}>
                  {(() => {
                    const other = active.customer_id === session.user.id ? active.technician : active.customer;
                    return (
                      <>
                        {other?.avatar_url ? (
                          <img
                            src={other.avatar_url}
                            alt={other.full_name || other.email}
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: "2px solid var(--border)"
                            }}
                          />
                        ) : (
                          <div style={{
                            width: 36,
                            height: 36,
                            borderRadius: "50%",
                            background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#fff",
                            fontWeight: 700,
                            fontSize: 14
                          }}>
                            {(other?.full_name || other?.email || "?")[0].toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>
                            {other?.full_name || other?.email || "Conversation"}
                          </div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                            {active.booking?.request?.final_design?.name || "Booking chat"}
                            {active.booking?.status ? ` · ${active.booking.status}` : ""}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <div ref={scrollRef} style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: 16,
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                  background: "linear-gradient(to bottom, rgba(196,135,154,0.02), transparent)"
                }}>
                  {messages.length === 0 && (
                    <div style={{ textAlign: "center", color: "var(--text-muted)", padding: "32px 16px" }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>👋</div>
                      <div style={{ fontSize: 13 }}>No messages yet. Start the conversation!</div>
                    </div>
                  )}
                  {messages.map((m) => {
                    const mine = m.sender_id === session.user.id;
                    return (
                      <div key={m.id} style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: mine ? "flex-end" : "flex-start",
                        animation: "slideIn 0.2s ease-out"
                      }}>
                        <div style={{
                          background: mine ? "linear-gradient(135deg, var(--mauve), var(--mauve-deep))" : "var(--bg-soft)",
                          color: mine ? "#fff" : "var(--text)",
                          borderRadius: mine ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                          padding: "10px 14px",
                          maxWidth: "75%",
                          boxShadow: mine ? "0 2px 8px rgba(196,135,154,0.2)" : "0 2px 8px rgba(0,0,0,0.05)",
                          position: "relative"
                        }}>
                          <div style={{ fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {m.body}
                          </div>
                          <div style={{
                            fontSize: 10,
                            opacity: 0.7,
                            marginTop: 6,
                            textAlign: mine ? "right" : "left"
                          }}>
                            {formatShortDateTime(m.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {sending && (
                    <div style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center"
                    }}>
                      <div style={{
                        background: "rgba(196,135,154,0.1)",
                        borderRadius: "16px 16px 4px 16px",
                        padding: "10px 14px",
                        display: "flex",
                        gap: 4
                      }}>
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--mauve)", animation: "pulse 1s infinite" }} />
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--mauve)", animation: "pulse 1s infinite 0.2s" }} />
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--mauve)", animation: "pulse 1s infinite 0.4s" }} />
                      </div>
                    </div>
                  )}
                  {typing && (
                    <div style={{ display: "flex", justifyContent: "flex-start" }}>
                      <div style={{
                        background: "var(--bg-soft)",
                        border: "1px solid var(--border)",
                        borderRadius: "16px 16px 16px 4px",
                        padding: "10px 14px",
                        display: "flex",
                        gap: 4,
                        alignItems: "center"
                      }}>
                        <span style={{ fontSize: 12, color: "var(--text-muted)", marginRight: 4 }}>typing</span>
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--mauve)", animation: "pulse 1s infinite" }} />
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--mauve)", animation: "pulse 1s infinite 0.2s" }} />
                        <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--mauve)", animation: "pulse 1s infinite 0.4s" }} />
                      </div>
                    </div>
                  )}
                </div>
                <form onSubmit={(e) => { e.preventDefault(); send(); }} style={{
                  display: "flex",
                  gap: 8,
                  padding: 14,
                  borderTop: "1px solid var(--border)",
                  background: "var(--bg-elevated)"
                }}>
                  <input
                    ref={inputRef}
                    value={text}
                    onChange={(e) => {
                      setText(e.target.value);
                      broadcastTyping(Boolean(e.target.value.trim())).catch(() => {});
                    }}
                    placeholder="Type a message…"
                    disabled={sending}
                    style={{
                      flex: 1,
                      padding: "12px 16px",
                      borderRadius: 999,
                      border: "1.5px solid var(--border-strong)",
                      background: "var(--bg)",
                      color: "var(--text)",
                      fontSize: 14,
                      outline: "none",
                      transition: "border-color 0.15s ease"
                    }}
                    onFocus={(e) => e.target.style.borderColor = "var(--mauve)"}
                    onBlur={(e) => {
                      e.target.style.borderColor = "var(--border-strong)";
                      broadcastTyping(false).catch(() => {});
                    }} />
                  <button
                    type="submit"
                    disabled={!text.trim() || sending}
                    style={{
                      padding: "12px 20px",
                      borderRadius: 999,
                      fontWeight: 600,
                      background: text.trim() && !sending ? "linear-gradient(135deg, var(--mauve), var(--mauve-deep))" : "var(--bg-soft)",
                      color: text.trim() && !sending ? "#fff" : "var(--text-muted)",
                      border: "none",
                      cursor: text.trim() && !sending ? "pointer" : "not-allowed",
                      transition: "all 0.15s ease",
                      fontSize: 14,
                      display: "flex",
                      alignItems: "center",
                      gap: 6
                    }}>
                    {sending ? "Sending..." : "Send"}
                    {!sending && text.trim() && <span>→</span>}
                  </button>
                </form>
              </>
            )}
          </Card>
        </div>
        {err && <div style={{ color: "#C02020", marginTop: 12 }}>{err}</div>}
      </div>
    </div>
  );
}

function groupThreadsByContact(threads, currentUserId) {
  const groups = new Map();
  for (const thread of threads || []) {
    const id = otherId(thread, currentUserId);
    const other = otherProfile(thread, currentUserId);
    if (!groups.has(id)) {
      groups.set(id, {
        id,
        name: other?.full_name || other?.email || "Conversation",
        email: other?.email || "",
        avatar_url: other?.avatar_url || "",
        threads: [],
      });
    }
    groups.get(id).threads.push(thread);
  }
  return Array.from(groups.values())
	    .map((group) => ({
	      ...group,
	      threads: group.threads.sort(sortThreads),
	      latestDate: latestThreadTime(group),
	    }))
    .sort((a, b) => latestThreadTime(b) - latestThreadTime(a));
}

function otherId(thread, currentUserId) {
  return thread.customer_id === currentUserId ? thread.technician_id : thread.customer_id;
}

function otherProfile(thread, currentUserId) {
  return thread.customer_id === currentUserId ? thread.technician : thread.customer;
}

function sortThreads(a, b) {
  return latestThreadTime(b) - latestThreadTime(a);
}

function latestThreadTime(threadOrGroup) {
  if (Array.isArray(threadOrGroup?.threads)) {
    return Math.max(...threadOrGroup.threads.map(latestThreadTime));
  }
  return new Date(threadOrGroup?.last_message_at || threadOrGroup?.booking?.created_at || 0).getTime();
}

function initialsFor(name = "") {
  const letters = String(name).trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("");
  return (letters || "C").toUpperCase();
}

function formatThreadStatus(raw = "") {
  return String(raw).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

function formatShortDateTime(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
